import { getShareFraction } from '../services/itemShares';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    ChevronDown,
    ChevronLeft,
    ChevronRight,
    ChevronUp,
    Edit3,
    Lock,
    Plus,
    Receipt,
    UserPlus,
    Users,
    X,
} from 'lucide-react';
import WebCta from './WebCta';
import WebHeaderIconButton from './WebHeaderIconButton';
import WebWhoHadThisSheet, { WhoHadThisParticipant, matchesWhoHadThisParticipant, getWhoHadThisParticipantCanonicalKey } from './WebWhoHadThisSheet';
import WebHowWasThisSharedSheet from './WebHowWasThisSharedSheet';
import WebCoverSomeoneSheet from './WebCoverSomeoneSheet';
import { normalizePhone } from '../services/firebase';
import { WebItemIconAvatar } from './WebItemIconAvatar';
import { resolveWebParticipantIdentity, getParticipantInitials, buildParticipantOrderMap, sortShareEntries } from '../utils/webParticipantAvatar';
import { triggerUiHaptic } from '../utils/webHaptics';
import {
    removeSessionViewerShareOptimistic,
    replaceSessionViewerShareOptimistic,
    resolveSessionViewerShare,
    roundClaimShareFraction,
} from '../services/itemShares';
import { computeParticipantTaxTipBreakdown, selectViewerTaxTipBreakdown } from '../services/receiptTaxTipBreakdown';

const roundMoney = (v: number) => Math.round(Number(v || 0) * 100) / 100;

function resolveSessionCoveredPersons({
    session,
    sessionInvites,
    participants,
    userPhone,
}: {
    session: any;
    sessionInvites?: any[];
    participants?: Record<string, any>;
    userPhone?: string;
}): WhoHadThisParticipant[] {
    const list: WhoHadThisParticipant[] = [];
    const coverageMap = session?.coverageMap || {};
    const coveredByMap = session?.coveredByMap || {};

    const findCoveredName = (pNorm: string) => {
        const inv = (sessionInvites || []).find((i: any) => normalizePhone(i?.phone || i?.phoneSnapshot || i?.id || '') === pNorm);
        const pData = participants?.[pNorm] || session?.participantsByPhone?.[pNorm] || {};
        return String(inv?.name || inv?.displayName || pData?.name || pData?.displayName || coverageMap[pNorm]?.coveredPersonName || 'Friend').trim();
    };

    const findCoveredPhoto = (pNorm: string) => {
        const inv = (sessionInvites || []).find((i: any) => normalizePhone(i?.phone || i?.phoneSnapshot || i?.id || '') === pNorm);
        const pData = participants?.[pNorm] || session?.participantsByPhone?.[pNorm] || {};
        return inv?.photoURL || inv?.photoUrl || pData?.photoURL || pData?.photoUrl || null;
    };

    Object.entries(coverageMap).forEach(([phone, rec]: [string, any]) => {
        const normCovBy = normalizePhone(rec?.coveredByPhone || rec?.coveredBy || '');
        if (userPhone && normCovBy === userPhone) {
            const pNorm = normalizePhone(phone) || phone;
            list.push({
                id: pNorm,
                name: findCoveredName(pNorm),
                phone: pNorm,
                photoURL: findCoveredPhoto(pNorm),
            });
        }
    });

    if (list.length === 0 && coveredByMap) {
        Object.entries(coveredByMap).forEach(([phone, byPhone]: [string, any]) => {
            if (userPhone && normalizePhone(String(byPhone)) === userPhone) {
                const pNorm = normalizePhone(phone) || phone;
                list.push({
                    id: pNorm,
                    name: findCoveredName(pNorm),
                    phone: pNorm,
                    photoURL: findCoveredPhoto(pNorm),
                });
            }
        });
    }

    return list;
}

export interface WebReviewItemsViewProps {
    session: any;
    invite: any;
    sessionInvites: any[];
    participants: Record<string, any>;
    onClose: () => void;
    onCommitClaims: (mutations: any[], coveragePayload?: { coverageMap: any; coveredByMap: any }, options?: { baseSessionVersion?: number }) => Promise<{ success?: boolean; conflicts?: any[] } | void>;
    onViewReceipt?: () => void;
    getItemIconEmoji?: (iconKey?: string, name?: string) => string;
}

export const WebReviewItemsView: React.FC<WebReviewItemsViewProps> = ({
    session,
    invite,
    sessionInvites,
    participants,
    onClose,
    onCommitClaims,
    onViewReceipt,
    getItemIconEmoji = () => '🍽️',
}) => {
    const userPhone = normalizePhone(invite?.phone || invite?.phoneSnapshot || '');
    const isOrganizer = Boolean(session?.organizerPhone && userPhone && normalizePhone(session.organizerPhone) === userPhone);
    const organizerPhoneNorm = normalizePhone(session?.organizerPhone || '');
    const organizerDisplayName = session?.organizerName || 'Organizer';
    const [isExiting, setIsExiting] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);
    const footerRef = useRef<HTMLDivElement>(null);
    const scrollContainerRef = useRef<HTMLDivElement | null>(null);
    const [footerHeight, setFooterHeight] = useState(280);

    const handleSafeClose = useCallback(() => {
        setIsExiting(true);
        setTimeout(() => {
            onClose();
        }, 240);
    }, [onClose]);

    // Initial snapshot tracking for mutations
    const originalSnapshotRef = useRef<{
        sessionVersion: number;
        itemVersionById: Record<string, number>;
        sharesByItemId: Record<string, Record<string, any>>;
    }>({
        sessionVersion: Number(session?.version || 0),
        itemVersionById: (session?.items || []).reduce((acc: any, it: any) => {
            if (it?.id) acc[it.id] = Number(it?.version || 0);
            return acc;
        }, {}),
        sharesByItemId: (session?.items || []).reduce((acc: any, it: any) => {
            if (it?.id) acc[it.id] = { ...(it?.shares || {}) };
            return acc;
        }, {}),
    });

    // Local mutable items state
    const [items, setItems] = useState<any[]>(() => {
        return (session?.items || []).map((it: any) => {
            const rawShares = it.shares || {};
            const cleanShares = session?.isGroupExpenseMode
                ? Object.fromEntries(
                    Object.entries(rawShares).map(([key, val]: [string, any]) => [
                        key,
                        typeof val === 'object' && val !== null ? { ...val, isLocked: false, paid: false } : val,
                    ])
                )
                : { ...rawShares };
            return {
                ...it,
                shares: cleanShares,
            };
        });
    });

    const [draftRebalanceModeByItem, setDraftRebalanceModeByItem] = useState<Record<string, 'equalize' | undefined>>({});
    const [isSaving, setIsSaving] = useState(false);
    const [showUnsavedDiscardModal, setShowUnsavedDiscardModal] = useState(false);

    // Active sheets state
    const [whoHadThisItem, setWhoHadThisItem] = useState<any | null>(null);
    const [howSharedItem, setHowSharedItem] = useState<any | null>(null);
    const [showCoverSheet, setShowCoverSheet] = useState(false);
    const [showCoveredItemsDetails, setShowCoveredItemsDetails] = useState(false);

    // Coverage state
    const [optedOutOfCoverage, setOptedOutOfCoverage] = useState(false);
    const [coveredPersons, setCoveredPersons] = useState<WhoHadThisParticipant[]>(() =>
        resolveSessionCoveredPersons({ session, sessionInvites, participants, userPhone })
    );

    useEffect(() => {
        if (!footerRef.current) return;
        const updateHeight = () => {
            if (footerRef.current) {
                setFooterHeight(footerRef.current.offsetHeight);
            }
        };
        updateHeight();
        if (typeof ResizeObserver !== 'undefined') {
            const ro = new ResizeObserver(() => updateHeight());
            ro.observe(footerRef.current);
            return () => ro.disconnect();
        }
    }, [coveredPersons.length, saveError]);

    const initialCoveredPhonesRef = useRef<Set<string>>(
        new Set(coveredPersons.map((p) => normalizePhone(p.phone) || p.phone))
    );

    // Check if viewer is covered by someone
    const viewerCoveredBy = useMemo(() => {
        if (optedOutOfCoverage || !userPhone) return null;
        const covRec = session?.coverageMap?.[userPhone];
        const covByRaw = invite?.coveredByName || covRec?.coveredByName || session?.coveredByMap?.[userPhone];
        if (!covByRaw) return null;
        const covPhone = normalizePhone(covRec?.coveredByPhone || invite?.coveredByPhone || '');
        const pData = covPhone ? participants[covPhone] : null;
        return {
            name: String(invite?.coveredByName || covRec?.coveredByName || pData?.name || 'Friend').trim(),
            phone: covPhone || '',
            photoURL: pData?.photoURL,
        };
    }, [invite, optedOutOfCoverage, participants, session, userPhone]);

    // All session participants
    const allSessionParticipants: WhoHadThisParticipant[] = useMemo(() => {
        const map = new Map<string, WhoHadThisParticipant>();
        const lockedPhones = new Set<string>();

        if (!session?.isGroupExpenseMode) {
            // Locked participants from invites
            (sessionInvites || []).forEach((inv: any) => {
                const rawPhone = inv?.phone || inv?.phoneSnapshot || inv?.id || '';
                const norm = normalizePhone(rawPhone);
                if (!norm) return;
                const isLocked = Boolean(
                    inv.isPaymentLocked ||
                    inv.isLocked ||
                    inv.isCoveredPaid ||
                    inv.status === 'paid' ||
                    inv.status === 'processing' ||
                    inv.status === 'settled_offline' ||
                    inv.status === 'verified'
                );
                if (isLocked) lockedPhones.add(norm);
            });

            // Coverage-based locks: covered by paid user, covered by organizer, or coverage marked as covered paid
            if (session?.coverageMap) {
                Object.entries(session.coverageMap).forEach(([targetPhone, covRec]: [string, any]) => {
                    const normTarget = normalizePhone(targetPhone);
                    if (!normTarget) return;
                    if (covRec?.isCoveredPaid === true) {
                        lockedPhones.add(normTarget);
                    } else {
                        const covBy = normalizePhone(String(covRec?.coveredByPhone || covRec?.coveredBy || ''));
                        if (covBy && (covBy === 'organizer' || (organizerPhoneNorm && covBy === organizerPhoneNorm) || lockedPhones.has(covBy))) {
                            lockedPhones.add(normTarget);
                        }
                    }
                });
            }
            if (session?.coveredByMap) {
                Object.entries(session.coveredByMap).forEach(([targetPhone, covByRaw]: [string, any]) => {
                    const normTarget = normalizePhone(targetPhone);
                    if (!normTarget) return;
                    const covBy = normalizePhone(String(covByRaw || ''));
                    if (covBy && (covBy === 'organizer' || (organizerPhoneNorm && covBy === organizerPhoneNorm) || lockedPhones.has(covBy))) {
                        lockedPhones.add(normTarget);
                    }
                });
            }
            (sessionInvites || []).forEach((inv: any) => {
                const rawPhone = inv?.phone || inv?.phoneSnapshot || inv?.id || '';
                const norm = normalizePhone(rawPhone);
                if (!norm) return;
                const covBy = normalizePhone(String(inv?.coveredByPhone || inv?.coveredBy || ''));
                if (covBy && (covBy === 'organizer' || (organizerPhoneNorm && covBy === organizerPhoneNorm) || lockedPhones.has(covBy))) {
                    lockedPhones.add(norm);
                }
            });
        }

        // 1. Viewer
        if (userPhone) {
            const vPhoto = invite?.photoURL || invite?.photoUrl || participants[userPhone]?.photoURL;
            map.set(userPhone, {
                id: userPhone,
                name: invite?.name || 'You',
                phone: userPhone,
                photoURL: vPhoto,
                isLocked: lockedPhones.has(userPhone),
                isDeclined: Boolean(invite?.status === 'declined' || invite?.isDeclined),
                status: invite?.status,
                uid: invite?.participantUserUid || invite?.participantUid || invite?.uid || invite?.userId || participants[userPhone]?.uid || participants[userPhone]?.userId,
            });
        }

        // 2. Organizer
        if (organizerPhoneNorm && organizerPhoneNorm !== userPhone) {
            const orgPhoto = session?.organizerPhotoURL || session?.organizerPhotoUrl || participants[organizerPhoneNorm]?.photoURL;
            map.set(organizerPhoneNorm, {
                id: organizerPhoneNorm,
                name: organizerDisplayName,
                phone: organizerPhoneNorm,
                photoURL: orgPhoto,
                isOrganizer: true,
                isLocked: lockedPhones.has(organizerPhoneNorm),
                uid: session?.organizerUid || session?.organizerUserId || session?.uid,
            });
        }

        // 3. Invites & Participants
        (sessionInvites || []).forEach((inv: any) => {
            const rawPhone = inv?.phone || inv?.phoneSnapshot || inv?.id || '';
            const norm = normalizePhone(rawPhone);
            if (!norm || map.has(norm)) return;
            const isLocked = lockedPhones.has(norm);
            const pData = participants[norm] || {};
            map.set(norm, {
                id: norm,
                name: inv?.name || inv?.displayName || pData?.name || 'Friend',
                phone: norm,
                photoURL: inv?.photoURL || inv?.photoUrl || pData?.photoURL,
                isLocked,
                isDeclined: Boolean(inv?.status === 'declined' || inv?.isDeclined),
                status: inv?.status,
                uid: inv?.participantUserUid || inv?.participantUid || inv?.uid || inv?.userId || pData?.uid || pData?.userId,
            });
        });

        Object.entries(participants || {}).forEach(([pPhone, pData]: [string, any]) => {
            const norm = normalizePhone(pPhone);
            if (!norm || map.has(norm)) return;
            const isLocked = lockedPhones.has(norm);
            map.set(norm, {
                id: norm,
                name: pData?.name || pData?.displayName || 'Friend',
                phone: norm,
                photoURL: pData?.photoURL || pData?.photoUrl,
                isLocked,
                isDeclined: Boolean(pData?.status === 'declined' || pData?.isDeclined),
                status: pData?.status,
                uid: pData?.uid || pData?.userId || pData?.participantUid || pData?.participantUserUid,
            });
        });

        return Array.from(map.values());
    }, [invite?.name, invite?.participantUid, invite?.participantUserUid, invite?.photoURL, invite?.photoUrl, invite?.uid, invite?.userId, organizerDisplayName, organizerPhoneNorm, participants, session?.organizerPhotoURL, session?.organizerPhotoUrl, session?.organizerUid, session?.organizerUserId, session?.uid, sessionInvites, userPhone]);

    const participantOrderMap = useMemo(() => {
        return buildParticipantOrderMap({
            session,
            invites: sessionInvites,
            participants: allSessionParticipants,
        });
    }, [allSessionParticipants, session, sessionInvites]);

    const getParticipantDisplay = useCallback((phoneOrId: string, fallbackName?: string | null) => {
        const norm = normalizePhone(phoneOrId) || phoneOrId;
        const isOrganizerKey = phoneOrId === 'organizer' || (organizerPhoneNorm && norm === organizerPhoneNorm);
        const pObj = allSessionParticipants.find((p) => matchesWhoHadThisParticipant(p, phoneOrId, organizerPhoneNorm));
        const resolved = resolveWebParticipantIdentity({
            participant: pObj || { phone: norm, isOrganizer: isOrganizerKey },
            fallbackName,
            viewerPhone: userPhone,
            viewerDisplayName: invite?.name || 'You',
            viewerPhotoURL: invite?.photoURL || invite?.photoUrl,
            organizerPhone: session?.organizerPhone,
            organizerName: organizerDisplayName,
            organizerPhotoURL: session?.organizerPhotoURL || session?.organizerPhotoUrl,
            session,
        });
        const isLocked = Boolean(
            pObj?.isLocked ||
            pObj?.isPaymentLocked ||
            pObj?.status === 'paid' ||
            pObj?.status === 'processing' ||
            pObj?.status === 'settled_offline' ||
            pObj?.status === 'verified'
        );
        return {
            name: norm === userPhone ? 'You' : (resolved.canonicalName || pObj?.name || 'Participant'),
            photoURL: resolved.photoURL || pObj?.photoURL,
            initials: resolved.initials,
            isLocked,
        };
    }, [allSessionParticipants, invite?.name, invite?.photoURL, invite?.photoUrl, organizerDisplayName, organizerPhoneNorm, session, userPhone]);

    const isItemFullyLocked = useCallback((item: any) => {
        if (session?.isGroupExpenseMode) return false;
        const shares = item?.shares || {};
        let lockedFraction = 0;
        Object.entries(shares).forEach(([key, val]: any) => {
            const frac = getShareFraction(val);
            if (frac > 0.0001) {
                const iden = getParticipantDisplay(key, val?.name);
                const isLocked = Boolean(iden?.isLocked || val?.isLocked || val?.paid);
                if (isLocked) {
                    lockedFraction += frac;
                }
            }
        });
        return lockedFraction >= 0.999;
    }, [getParticipantDisplay, session?.isGroupExpenseMode]);

    // Categorize items
    const { assignedToYou, coveredItems, availableToClaim, claimedByOthers } = useMemo(() => {
        const assigned: any[] = [];
        const coveredList: any[] = [];
        const available: any[] = [];
        const others: any[] = [];

        const coveredPhoneSet = new Set(coveredPersons.map((p) => normalizePhone(p.phone) || p.phone));

        (items || []).forEach((item: any) => {
            const rawShares = item.shares || {};
            const shares = session?.isGroupExpenseMode
                ? Object.fromEntries(
                    Object.entries(rawShares).map(([key, val]: [string, any]) => [
                        key,
                        typeof val === 'object' && val !== null ? { ...val, isLocked: false, paid: false } : val,
                    ])
                )
                : rawShares;

            // 1. Viewer share
            const myShare = resolveSessionViewerShare({
                shares,
                isOrganizer,
                viewerPhone: userPhone,
                organizerPhone: session?.organizerPhone,
            });
            const viewerFraction = roundClaimShareFraction(getShareFraction(myShare));
            const hasViewerShare = viewerFraction > 0.0001;

            if (hasViewerShare) {
                const rawShareEntries = Object.entries(shares).filter(([_, s]: any) => getShareFraction(s) > 0.0001);
                const sortedShareEntries = sortShareEntries(rawShareEntries, participantOrderMap);
                assigned.push({
                    ...item,
                    viewerFraction,
                    shareEntries: sortedShareEntries,
                });
            }

            // 2. Covered persons share
            let itemCoveredFraction = 0;
            const itemCoveredClaimants: any[] = [];

            Object.entries(shares).forEach(([k, val]: [string, any]) => {
                const normK = normalizePhone(k) || k;
                if (coveredPhoneSet.has(normK)) {
                    const frac = getShareFraction(val);
                    if (frac > 0.0001) {
                        itemCoveredFraction += frac;
                        const pObj = allSessionParticipants.find((p) => (normalizePhone(p.phone || p.id) || p.id) === normK);
                        itemCoveredClaimants.push({
                            phone: normK,
                            name: pObj?.name || 'Friend',
                            fraction: frac,
                            photoURL: pObj?.photoURL,
                        });
                    }
                }
            });

            const hasCoveredShare = itemCoveredFraction > 0.0001;
            if (hasCoveredShare) {
                coveredList.push({
                    ...item,
                    coveredFraction: itemCoveredFraction,
                    coveredClaimants: itemCoveredClaimants,
                });
            }

            // 3. Availability and Others partitioning
            const totalAssignedFraction = Object.values(shares).reduce<number>(
                (sum, s: any) => sum + getShareFraction(s),
                0
            );
            const hasUnclaimedRemainder = totalAssignedFraction < 0.999;
            const hasOthersShare = Object.entries(shares).some(([k, val]: any) => {
                const normK = normalizePhone(k) || k;
                const isViewer = normK === userPhone || (isOrganizer && (normK === 'organizer' || normK === normalizePhone(session?.organizerPhone || '')));
                const isCovered = coveredPhoneSet.has(normK);
                return !isViewer && !isCovered && getShareFraction(val) > 0.0001;
            });

            if (!hasViewerShare && !hasCoveredShare) {
                if (hasUnclaimedRemainder) {
                    available.push(item);
                } else if (hasOthersShare) {
                    others.push(item);
                }
            }
        });

        return {
            assignedToYou: assigned,
            coveredItems: coveredList,
            availableToClaim: available,
            claimedByOthers: others,
        };
    }, [allSessionParticipants, coveredPersons, isOrganizer, items, session?.organizerPhone, userPhone]);

    const coveredSectionsByPerson = useMemo(() => {
        if (!coveredPersons || coveredPersons.length === 0) return [];
        return coveredPersons
            .map((person) => {
                const normPhone = normalizePhone(person.phone || person.id || '');
                const personItems: Array<{
                    item: any;
                    personFraction: number;
                    coveredPortionDollar: number;
                }> = [];
                let personSubtotal = 0;

                (items || []).forEach((item: any) => {
                    const shares = item.shares || {};
                    let pFraction = 0;
                    Object.entries(shares).forEach(([k, val]: [string, any]) => {
                        if ((normalizePhone(k) || k) === normPhone) {
                            const frac = getShareFraction(val);
                            if (frac > 0.0001) pFraction += frac;
                        }
                    });
                    if (pFraction > 0.0001) {
                        const coveredPortionDollar = roundMoney(Number(item.amount || 0) * pFraction);
                        personSubtotal += coveredPortionDollar;
                        personItems.push({
                            item,
                            personFraction: pFraction,
                            coveredPortionDollar,
                        });
                    }
                });

                return {
                    person,
                    items: personItems,
                    subtotal: roundMoney(personSubtotal),
                };
            })
            .filter((sec) => sec.items.length > 0);
    }, [coveredPersons, items]);

    // Check for changes
    const buildMutations = useCallback(() => {
        const original = originalSnapshotRef.current;
        const mutations: any[] = [];

        items.forEach((item: any) => {
            const itemId = String(item?.id || '');
            if (!itemId) return;

            const baseItemVersion = Number(original.itemVersionById[itemId] || 0);
            const currentShares = item?.shares || {};
            const originalShares = original.sharesByItemId?.[itemId] || {};

            const leftKeys = Object.keys(currentShares).filter((k) => getShareFraction(currentShares[k]) > 0.0001);
            const rightKeys = Object.keys(originalShares).filter((k) => getShareFraction(originalShares[k]) > 0.0001);
            let sharesChanged = leftKeys.length !== rightKeys.length;
            if (!sharesChanged) {
                for (const key of leftKeys) {
                    const left = currentShares[key];
                    const right = originalShares[key];
                    if (!right || Math.abs(getShareFraction(left) - getShareFraction(right)) > 0.0001 || String(left?.mode || 'equal') !== String(right?.mode || 'equal') || String(left?.name || '') !== String(right?.name || '') || String(left?.coveredForPhone || '') !== String(right?.coveredForPhone || '')) {
                        sharesChanged = true;
                        break;
                    }
                }
            }

            const hasRebalanceIntent = Boolean(draftRebalanceModeByItem[itemId]);
            if (sharesChanged || hasRebalanceIntent) {
                mutations.push({
                    itemId,
                    operation: 'set_shares',
                    shares: currentShares,
                    baseItemVersion,
                    ...(draftRebalanceModeByItem[itemId] ? { rebalanceMode: draftRebalanceModeByItem[itemId] } : {}),
                });
            }
        });

        return mutations;
    }, [draftRebalanceModeByItem, items]);

    const hasCoverageChanges = useMemo(() => {
        if (optedOutOfCoverage) return true;
        const currentCoveredPhones = new Set(coveredPersons.map((p) => normalizePhone(p.phone) || p.phone));
        const initSet = initialCoveredPhonesRef.current;
        if (currentCoveredPhones.size !== initSet.size) return true;
        for (const phone of currentCoveredPhones) {
            if (!initSet.has(phone)) return true;
        }
        return false;
    }, [coveredPersons, optedOutOfCoverage]);

    const hasClaimChanges = useMemo(() => buildMutations().length > 0, [buildMutations]);
    const hasUnsavedChanges = hasClaimChanges || hasCoverageChanges;

    // Reconcile clean draft with incoming authoritative snapshot from server
    useEffect(() => {
        if (!hasUnsavedChanges && session?.items) {
            setItems((session.items || []).map((it: any) => {
                const rawShares = it.shares || {};
                const cleanShares = session?.isGroupExpenseMode
                    ? Object.fromEntries(
                        Object.entries(rawShares).map(([key, val]: [string, any]) => [
                            key,
                            typeof val === 'object' && val !== null ? { ...val, isLocked: false, paid: false } : val,
                        ])
                    )
                    : { ...rawShares };
                return {
                    ...it,
                    shares: cleanShares,
                };
            }));
            originalSnapshotRef.current = {
                sessionVersion: Number(session?.version || 0),
                itemVersionById: (session?.items || []).reduce((acc: any, it: any) => {
                    if (it?.id) acc[it.id] = Number(it?.version || 0);
                    return acc;
                }, {}),
                sharesByItemId: (session?.items || []).reduce((acc: any, it: any) => {
                    if (it?.id) acc[it.id] = { ...(it?.shares || {}) };
                    return acc;
                }, {}),
            };
        }
        if (!hasCoverageChanges) {
            const authoritativeCovered = resolveSessionCoveredPersons({
                session,
                sessionInvites,
                participants,
                userPhone,
            });
            setCoveredPersons(authoritativeCovered);
            initialCoveredPhonesRef.current = new Set(
                authoritativeCovered.map((p) => normalizePhone(p.phone) || p.phone)
            );
        }
    }, [hasCoverageChanges, hasUnsavedChanges, participants, session, sessionInvites, userPhone]);

    // Financial calculations
    const { viewerOwnTotal, viewerCoveredTotal, viewerExtrasTotal, viewerNetTotal } = useMemo(() => {
        let ownTotal = 0;
        let covTotal = 0;

        assignedToYou.forEach((item) => {
            ownTotal += roundMoney(Number(item.amount || 0) * Number(item.viewerFraction || 0));
        });

        coveredItems.forEach((item) => {
            covTotal += roundMoney(Number(item.amount || 0) * Number(item.coveredFraction || 0));
        });

        const claimedBase = roundMoney(ownTotal + covTotal);
        let extrasTotal = 0;

        if (claimedBase > 0.0001) {
            const totalItemAmount = (items || []).reduce((sum: number, it: any) => sum + Number(it?.amount || 0), 0);
            const tax = Number(session?.tax || 0);
            const tip = Number(session?.tip || session?.tipAmount || 0);
            const fees = Number(session?.fees || 0);
            const discounts = Number(session?.discounts || 0);
            const deductions = Number(session?.deductions || 0);
            const netExtras = tax + tip + fees - discounts - deductions;

            const ratio = totalItemAmount > 0 ? (claimedBase / totalItemAmount) : 0;
            const proportionalExtras = roundMoney(netExtras * ratio);

            const rawInviteFee = invite?.payeeFeeAmount !== undefined
                ? Number(invite.payeeFeeAmount)
                : (invite?.feeAmountOwedCents !== undefined
                    ? Number(invite.feeAmountOwedCents) / 100
                    : (session?.platformFeePerPayerCents !== undefined
                        ? Number(session.platformFeePerPayerCents) / 100
                        : 0));
            const platformFee = Number.isFinite(rawInviteFee) && rawInviteFee > 0 ? roundMoney(rawInviteFee) : 0;
            extrasTotal = roundMoney(proportionalExtras + platformFee);
        }

        const calculatedNetTotal = viewerCoveredBy ? 0 : Math.max(0, roundMoney(claimedBase + extrasTotal));
        const myInviteAmount = Number(invite?.appliedTotalAmount ?? invite?.amount ?? 0);

        // If no local modifications have been made, synchronize with invite amount to guarantee exact cent match
        const netTotal = (!hasClaimChanges && !hasCoverageChanges && myInviteAmount > 0 && !viewerCoveredBy)
            ? myInviteAmount
            : calculatedNetTotal;

        // Synchronize extras if netTotal is locked to myInviteAmount
        const finalExtrasTotal = (!hasClaimChanges && !hasCoverageChanges && myInviteAmount > 0 && !viewerCoveredBy)
            ? Math.max(0, roundMoney(myInviteAmount - claimedBase))
            : extrasTotal;

        return {
            viewerOwnTotal: roundMoney(ownTotal),
            viewerCoveredTotal: roundMoney(covTotal),
            viewerExtrasTotal: finalExtrasTotal,
            viewerNetTotal: netTotal,
        };
    }, [assignedToYou, coveredItems, hasClaimChanges, hasCoverageChanges, invite?.amount, invite?.appliedTotalAmount, invite?.feeAmountOwedCents, invite?.payeeFeeAmount, items, session?.deductions, session?.discounts, session?.fees, session?.platformFeePerPayerCents, session?.tax, session?.tip, session?.tipAmount, viewerCoveredBy]);

    // Action Handlers
    const handleJoinSplit = useCallback((itemId: string, portionAmount: number, forceEqual = true) => {
        triggerUiHaptic('impactLight');
        setItems((prev) =>
            prev.map((item) => {
                if (String(item.id) !== String(itemId)) return item;
                const nextShares = replaceSessionViewerShareOptimistic({
                    shares: item.shares || {},
                    fraction: item.amount > 0 ? Math.min(1, portionAmount / item.amount) : 0.5,
                    isOrganizer,
                    viewerPhone: userPhone,
                    organizerPhone: session?.organizerPhone,
                    name: isOrganizer ? organizerDisplayName : (invite?.name || 'You'),
                    forceEqualJoin: forceEqual,
                });
                return {
                    ...item,
                    shares: nextShares,
                };
            })
        );
        setDraftRebalanceModeByItem((prev) => ({
            ...prev,
            [itemId]: forceEqual ? 'equalize' : 'custom',
        }));
        setTimeout(() => {
            scrollContainerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
        }, 120);
    }, [invite?.name, isOrganizer, organizerDisplayName, session?.organizerPhone, userPhone]);

    const handleClaimFull = useCallback((itemId: string) => {
        triggerUiHaptic('impactLight');
        setItems((prev) =>
            prev.map((item) => {
                if (String(item.id) !== String(itemId)) return item;
                const nextShares = replaceSessionViewerShareOptimistic({
                    shares: item.shares || {},
                    fraction: 1.0,
                    isOrganizer,
                    viewerPhone: userPhone,
                    organizerPhone: session?.organizerPhone,
                    name: isOrganizer ? organizerDisplayName : (invite?.name || 'You'),
                });
                return {
                    ...item,
                    shares: nextShares,
                };
            })
        );
        setTimeout(() => {
            scrollContainerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
        }, 120);
    }, [invite?.name, isOrganizer, organizerDisplayName, session?.organizerPhone, userPhone]);

    const handleSaveAssignment = useCallback(({ itemId, shareMap }: { itemId: string; shareMap: any }) => {
        triggerUiHaptic('impactMedium');
        setDraftRebalanceModeByItem((prev) => {
            const next = { ...prev };
            delete next[itemId];
            return next;
        });

        setItems((prev) =>
            prev.map((item) => {
                if (String(item.id) !== String(itemId)) return item;
                return {
                    ...item,
                    shares: shareMap,
                };
            })
        );
        setTimeout(() => {
            scrollContainerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
        }, 120);
    }, []);

    const handleSaveAndCommit = async () => {
        triggerUiHaptic('impactMedium');
        const mutations = buildMutations();
        if (mutations.length === 0 && !hasCoverageChanges) {
            handleSafeClose();
            return;
        }

        setIsSaving(true);
        setSaveError(null);
        try {
            const coverageMapPayload: Record<string, any> = {};
            const coveredByMapPayload: Record<string, any> = {};
            const myPhone = isOrganizer ? (session?.organizerPhone || 'organizer') : userPhone;
            const myName = isOrganizer ? organizerDisplayName : (invite?.name || 'You');

            if (hasCoverageChanges) {
                if (optedOutOfCoverage && userPhone) {
                    coverageMapPayload[userPhone] = null;
                    coveredByMapPayload[userPhone] = '';
                }

                if (coveredPersons.length > 0) {
                    coveredPersons.forEach((person) => {
                        const norm = normalizePhone(person.phone) || person.phone;
                        coverageMapPayload[norm] = {
                            coveredByPhone: myPhone,
                            coveredByName: myName,
                            coveredAt: new Date().toISOString(),
                        };
                        coveredByMapPayload[norm] = myPhone;
                    });
                }

                initialCoveredPhonesRef.current.forEach((phone) => {
                    const norm = normalizePhone(phone) || phone;
                    const stillCovered = coveredPersons.some((cp) => (normalizePhone(cp.phone) || cp.phone) === norm);
                    if (!stillCovered) {
                        coverageMapPayload[norm] = null;
                        coveredByMapPayload[norm] = '';
                    }
                });
            }

            const commitRes = await onCommitClaims(
                mutations,
                hasCoverageChanges
                    ? {
                        coverageMap: coverageMapPayload,
                        coveredByMap: coveredByMapPayload,
                    }
                    : undefined,
                {
                    baseSessionVersion: originalSnapshotRef.current.sessionVersion,
                }
            );
            if (commitRes && commitRes.success === false) {
                return;
            }
            initialCoveredPhonesRef.current = new Set(
                coveredPersons.map((p) => normalizePhone(p.phone) || p.phone)
            );
            handleSafeClose();
        } catch (err: any) {
            console.error('Save claims error:', err);
            setSaveError(err?.message || 'Failed to save changes. Please try again.');
        } finally {
            setIsSaving(false);
        }
    };

    const handleBackClick = () => {
        triggerUiHaptic('selection');
        if (hasUnsavedChanges) {
            setShowUnsavedDiscardModal(true);
        } else {
            handleSafeClose();
        }
    };

    const receiptUrl = session?.receiptThumbnailURL || session?.receiptThumbnailUrl || session?.receipt?.thumbnailUrl || (session as any)?.aiData?.thumbnailDataUrl || session?.receiptImageURL || session?.receiptImageUrl || session?.receipt?.imageUrl;

    return (
        <div className={`fixed inset-0 z-[70] bg-[#F8FAFC] flex flex-col overflow-x-hidden overscroll-contain transition-transform duration-240 ease-[cubic-bezier(0.16,1,0.3,1)] ${
            isExiting ? 'translate-x-full' : 'translate-x-0 animate-slideLeft'
        }`}>
            {/* Top Navigation Bar */}
            <div className="sticky top-0 z-20 bg-white/95 backdrop-blur-md border-b border-slate-100 px-4 py-3 pt-[max(0.75rem,env(safe-area-inset-top))]">
                <div className="max-w-md mx-auto flex items-center justify-between gap-3">
                    <WebHeaderIconButton
                        onClick={handleBackClick}
                        aria-label="Go back"
                    />
                    <div className="min-w-0 flex-1 text-center">
                        <h2 className="text-base font-extrabold text-slate-900 leading-tight">Review Items</h2>
                        <p className="text-[11px] font-semibold text-slate-500 truncate mt-0.5">
                            {session.title || session.name || session.site || (session.isGroupExpenseMode ? 'Expense' : 'Receipt Split')}
                        </p>
                    </div>
                    <div className="w-[38px] shrink-0" />
                </div>
            </div>

            {/* Main Scrollable Content */}
            <div
                ref={scrollContainerRef}
                className="flex-1 overflow-y-auto overscroll-contain px-4 pt-4"
                style={{ paddingBottom: `${(footerHeight || 280) + 32}px` }}
            >
                <div className="max-w-md mx-auto space-y-5">
                    {/* Person-level Covering Banner OR Covered-by Hero Banner */}
                    {viewerCoveredBy ? (
                        <div className="bg-emerald-50/70 border border-emerald-200/80 rounded-2xl p-4 flex items-center justify-between gap-3.5 shadow-sm">
                            <div className="w-10 h-10 rounded-full bg-[#D7E1EA] text-[#475569] border-2 border-white font-bold text-xs flex items-center justify-center overflow-hidden shrink-0 shadow-xs">
                                {viewerCoveredBy.photoURL ? (
                                    <img src={viewerCoveredBy.photoURL} alt={viewerCoveredBy.name} className="w-full h-full object-cover" />
                                ) : (
                                    viewerCoveredBy.name?.slice(0, 2).toUpperCase() || 'F'
                                )}
                            </div>
                            <div className="min-w-0 flex-1">
                                <p className="font-extrabold text-sm text-emerald-950">Covered by {viewerCoveredBy.name}</p>
                                <p className="text-xs text-emerald-700 font-medium mt-0.5 leading-snug">
                                    {viewerCoveredBy.name} is paying for your items (${(viewerOwnTotal + viewerExtrasTotal).toFixed(2)}).
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={() => {
                                    triggerUiHaptic('selection');
                                    setOptedOutOfCoverage(true);
                                }}
                                className="px-3 py-1.5 rounded-full bg-white border border-emerald-200 text-xs font-bold text-emerald-800 hover:bg-emerald-50 transition-colors shrink-0"
                            >
                                Change
                            </button>
                        </div>
                    ) : (
                        <div
                            onClick={() => {
                                triggerUiHaptic('selection');
                                setShowCoverSheet(true);
                            }}
                            className="bg-white rounded-3xl p-4 flex items-center justify-between gap-3 cursor-pointer group active:scale-[0.99]"
                        >
                            <div className="flex items-center gap-3 min-w-0 flex-1">
                                {coveredPersons.length > 0 ? (
                                    <div className="flex -space-x-2 shrink-0">
                                        {coveredPersons.slice(0, 2).map((person) => {
                                            const iden = getParticipantDisplay(person.phone || person.id);
                                            return (
                                                <div key={person.phone || person.id} className="w-9 h-9 rounded-full bg-[#D7E1EA] text-[#475569] font-bold text-xs flex items-center justify-center border-2 border-white overflow-hidden shrink-0">
                                                    {iden.photoURL ? (
                                                        <img src={iden.photoURL} alt={iden.name} className="w-full h-full object-cover" />
                                                    ) : (
                                                        <span>{iden.initials}</span>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    <div className="w-9 h-9 rounded-full bg-slate-100 text-slate-600 flex items-center justify-center shrink-0">
                                        <UserPlus className="w-4 h-4" />
                                    </div>
                                )}
                                <div className="min-w-0 flex-1">
                                    <p className="font-extrabold text-sm text-slate-900 truncate">
                                        {coveredPersons.length === 0
                                            ? 'Paying for someone else?'
                                            : coveredPersons.length === 1
                                            ? `Covering ${coveredPersons[0].name}`
                                            : `Covering ${coveredPersons.length} people`}
                                    </p>
                                    <p className="text-xs text-slate-500 font-medium mt-0.5 truncate">
                                        {coveredPersons.length === 0
                                            ? "Tap to pay for a friend's share"
                                            : 'Their items are included in your total'}
                                    </p>
                                </div>
                            </div>
                            <div className="flex items-center gap-1 bg-slate-100 hover:bg-slate-200 px-3 py-1.5 rounded-full text-xs font-bold text-slate-600 transition-colors shrink-0">
                                <span>{coveredPersons.length > 0 ? 'Change' : 'Select'}</span>
                                <ChevronRight className="w-3.5 h-3.5 text-slate-400 stroke-[2.5]" />
                            </div>
                        </div>
                    )}

                    {/* Section 1: Your items */}
                    <div className="space-y-2.5">
                        <div className="flex items-center gap-2 px-1">
                            <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">
                                YOUR ITEMS
                            </span>
                            <span className="w-5 h-5 rounded-full bg-[#E2E8F0] text-[#334155] font-bold text-[11px] flex items-center justify-center">
                                {assignedToYou.length}
                            </span>
                        </div>

                        {assignedToYou.length === 0 ? (
                            <div className="bg-white rounded-3xl p-6 text-center space-y-1">
                                <p className="font-extrabold text-sm text-slate-800">No items assigned to you yet</p>
                                <p className="text-xs text-slate-500 font-medium leading-relaxed">
                                    Tap "I had this" on any available item below to claim your share.
                                </p>
                            </div>
                        ) : (
                            <div className="bg-white rounded-3xl overflow-hidden divide-y divide-slate-100/70">
                                {assignedToYou.map((item) => {
                                    const itemShareDollar = item.amount * item.viewerFraction;
                                    const isFullSingleClaim = item.viewerFraction >= 0.999 && (item.shareEntries || []).length <= 1;

                                    return (
                                        <div key={item.id} className="p-4 flex items-center gap-3.5 hover:bg-slate-50/50 transition-colors">
                                            <WebItemIconAvatar name={item.name} iconKey={item.iconKey} size={40} />
                                            <div className="min-w-0 flex-1 space-y-1.5">
                                                <div className="flex items-center justify-between gap-2">
                                                    <p className="font-extrabold text-sm text-slate-900 truncate">{item.name}</p>
                                                    <p className="font-black text-sm text-slate-900 shrink-0">${itemShareDollar.toFixed(2)}</p>
                                                </div>
                                                <div className="flex items-center justify-between gap-2">
                                                    <p className="text-xs font-medium text-slate-500 truncate">
                                                        {isFullSingleClaim
                                                            ? 'Full item'
                                                            : `Your portion: $${itemShareDollar.toFixed(2)} of $${Number(item.amount || 0).toFixed(2)}`}
                                                    </p>
                                                    {isFullSingleClaim && (
                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                triggerUiHaptic('selection');
                                                                setWhoHadThisItem(item);
                                                            }}
                                                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold transition-colors shrink-0"
                                                        >
                                                            <Edit3 className="w-3 h-3" />
                                                            <span>Edit</span>
                                                        </button>
                                                    )}
                                                </div>
                                                {!isFullSingleClaim && (
                                                    <div className="flex items-center justify-between gap-2 pt-1.5">
                                                        {item.shareEntries && item.shareEntries.length > 0 ? (
                                                            <div className="flex items-center gap-1.5 overflow-visible shrink-0">
                                                                {item.shareEntries.slice(0, 3).map(([sharePhone, _sh]: any) => {
                                                                    const iden = getParticipantDisplay(sharePhone, _sh?.name);
                                                                    return (
                                                                        <div
                                                                            key={sharePhone}
                                                                            className={`w-[26px] h-[26px] rounded-full bg-[#E2E8F0] text-slate-700 font-bold text-[10.5px] leading-none flex items-center justify-center overflow-hidden shrink-0 ${
                                                                                iden.isLocked || _sh?.isLocked ? 'opacity-60 ring-1 ring-slate-300' : ''
                                                                            }`}
                                                                        >
                                                                            {iden.photoURL ? (
                                                                                <img src={iden.photoURL} alt={iden.name} className="w-full h-full object-cover" />
                                                                            ) : (
                                                                                <span className="tracking-tight">{iden.initials}</span>
                                                                            )}
                                                                        </div>
                                                                    );
                                                                })}
                                                                {item.shareEntries.length > 3 && (
                                                                    <div className="w-[26px] h-[26px] rounded-full bg-slate-100 text-slate-600 font-bold text-[10px] leading-none flex items-center justify-center shrink-0">
                                                                        +{item.shareEntries.length - 3}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        ) : <div />}
                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                triggerUiHaptic('selection');
                                                                setWhoHadThisItem(item);
                                                            }}
                                                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold transition-colors shrink-0"
                                                        >
                                                            <Edit3 className="w-3 h-3" />
                                                            <span>Edit</span>
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    {/* Section 2: Covering for Friends (compact summary + disclosure) */}
                    {coveredSectionsByPerson.length > 0 && (
                        <div className="space-y-2.5">
                            <div className="flex items-center gap-2 px-1">
                                <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">
                                    {coveredSectionsByPerson.length === 1
                                        ? `COVERING FOR ${coveredSectionsByPerson[0].person.name.toUpperCase()}`
                                        : `COVERING ${coveredSectionsByPerson.length} PEOPLE`}
                                </span>
                                <span className="w-5 h-5 rounded-full bg-[#E2E8F0] text-[#334155] font-bold text-[11px] flex items-center justify-center">
                                    {coveredSectionsByPerson.reduce((sum, s) => sum + s.items.length, 0)}
                                </span>
                            </div>

                            <div className="bg-white rounded-3xl overflow-hidden divide-y divide-slate-100/70 shadow-sm border border-slate-100/80">
                                {coveredSectionsByPerson.map((section) => {
                                    const iden = getParticipantDisplay(section.person.phone, section.person.name);
                                    return (
                                        <div
                                            key={`cov_sum_${section.person.phone || section.person.id}`}
                                            className="px-4 py-3 flex items-center justify-between gap-3"
                                        >
                                            <div className="flex items-center gap-2.5 min-w-0">
                                                <div className="w-7 h-7 rounded-full bg-[#E2E8F0] text-slate-700 font-bold text-xs flex items-center justify-center overflow-hidden shrink-0">
                                                    {section.person.photoURL || iden.photoURL ? (
                                                        <img src={section.person.photoURL || iden.photoURL} alt={section.person.name} className="w-full h-full object-cover" />
                                                    ) : (
                                                        <span>{iden.initials}</span>
                                                    )}
                                                </div>
                                                <span className="font-semibold text-sm text-slate-900 truncate">
                                                    {section.person.name}
                                                </span>
                                            </div>
                                            <div className="flex items-center shrink-0">
                                                <span className="font-bold text-sm text-slate-900 font-mono">
                                                    ${section.subtotal.toFixed(2)}
                                                </span>
                                            </div>
                                        </div>
                                    );
                                })}

                                <button
                                    type="button"
                                    onClick={() => {
                                        triggerUiHaptic('selection');
                                        setShowCoveredItemsDetails(!showCoveredItemsDetails);
                                    }}
                                    className="w-full py-3 px-4 flex items-center justify-center gap-1.5 text-xs font-semibold text-slate-600 hover:text-slate-900 hover:bg-slate-50 transition-colors cursor-pointer"
                                >
                                    <span>{showCoveredItemsDetails ? 'Hide covered items' : 'View covered items'}</span>
                                    {showCoveredItemsDetails ? (
                                        <ChevronUp className="w-4 h-4 text-slate-500 stroke-[2.2]" />
                                    ) : (
                                        <ChevronDown className="w-4 h-4 text-slate-500 stroke-[2.2]" />
                                    )}
                                </button>
                            </div>

                            {showCoveredItemsDetails && (
                                <div className="space-y-3 pt-1">
                                    {coveredSectionsByPerson.map((section) => (
                                        <div key={`cov_detail_section_${section.person.phone || section.person.id}`} className="space-y-2">
                                            {coveredSectionsByPerson.length > 1 && (
                                                <div className="px-1">
                                                    <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">
                                                        {section.person.name}&apos;s items
                                                    </span>
                                                </div>
                                            )}
                                            <div className="bg-white rounded-3xl overflow-hidden divide-y divide-slate-100/70 shadow-sm border border-slate-100/80">
                                                {section.items.map(({ item, coveredPortionDollar }) => (
                                                    <div key={`cov_${section.person.phone}_${item.id}`} className="p-4 flex items-center gap-3.5 hover:bg-slate-50/50 transition-colors">
                                                        <WebItemIconAvatar name={item.name} iconKey={item.iconKey} size={40} />
                                                        <div className="min-w-0 flex-1 space-y-1">
                                                            <div className="flex items-center justify-between gap-2">
                                                                <p className="font-extrabold text-sm text-slate-900 truncate">{item.name}</p>
                                                                <p className="font-black text-sm text-slate-900 shrink-0">${coveredPortionDollar.toFixed(2)}</p>
                                                            </div>
                                                            <div className="flex items-center justify-between gap-2">
                                                                <p className="text-xs font-medium text-slate-500 truncate">
                                                                    Covered portion: ${coveredPortionDollar.toFixed(2)} of ${Number(item.amount || 0).toFixed(2)}
                                                                </p>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => {
                                                                        triggerUiHaptic('selection');
                                                                        setWhoHadThisItem(item);
                                                                    }}
                                                                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold transition-colors shrink-0 cursor-pointer"
                                                                >
                                                                    <Edit3 className="w-3 h-3" />
                                                                    <span>Edit</span>
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Section 3: Available to claim */}
                    {availableToClaim.length > 0 && (
                        <div className="space-y-2.5">
                            <div className="flex items-center gap-2 px-1">
                                <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">
                                    AVAILABLE TO CLAIM
                                </span>
                                <span className="w-5 h-5 rounded-full bg-[#E2E8F0] text-[#334155] font-bold text-[11px] flex items-center justify-center">
                                    {availableToClaim.length}
                                </span>
                            </div>

                            <div className="bg-white rounded-3xl overflow-hidden divide-y divide-slate-100/70">
                                {availableToClaim.map((item) => {
                                    const shares = item.shares || {};
                                    const totalAssignedFraction = Object.values(shares).reduce<number>((s, v: any) => s + getShareFraction(v), 0);
                                    const remainingDollars = Math.max(0, item.amount * (1 - totalAssignedFraction));
                                    const rawClaimants = Object.entries(shares).filter(([_, v]: any) => getShareFraction(v) > 0.0001);
                                    const claimants = sortShareEntries(rawClaimants, participantOrderMap);

                                    return (
                                        <div
                                            key={item.id}
                                            onClick={() => {
                                                triggerUiHaptic('selection');
                                                setHowSharedItem(item);
                                            }}
                                            className="p-4 flex items-center gap-3.5 hover:bg-slate-50 transition-colors cursor-pointer group active:scale-[0.99]"
                                        >
                                            <WebItemIconAvatar name={item.name} iconKey={item.iconKey} size={40} />
                                            <div className="min-w-0 flex-1 space-y-1.5">
                                                <div className="flex items-center justify-between gap-2">
                                                    <p className="font-extrabold text-sm text-slate-900 truncate">{item.name}</p>
                                                    <p className="font-black text-sm text-slate-700 shrink-0">${Number(item.amount || 0).toFixed(2)}</p>
                                                </div>
                                                <div className="flex items-center justify-between gap-2">
                                                    <p className="text-xs font-semibold text-slate-500 truncate">
                                                        ${remainingDollars.toFixed(2)} remaining
                                                    </p>
                                                    {claimants.length === 0 && (
                                                        <div className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-900 text-xs font-bold transition-colors shrink-0">
                                                            <Plus className="w-3 h-3 stroke-[2.5]" />
                                                            <span>I had this</span>
                                                        </div>
                                                    )}
                                                </div>
                                                {claimants.length > 0 && (
                                                    <div className="flex items-center justify-between gap-2 pt-1.5">
                                                        <div className="flex items-center gap-1.5 overflow-visible shrink-0">
                                                            {claimants.slice(0, 3).map(([ph, _sh]: any) => {
                                                                const iden = getParticipantDisplay(ph, _sh?.name);
                                                                return (
                                                                    <div
                                                                        key={ph}
                                                                        className={`w-[26px] h-[26px] rounded-full bg-[#E2E8F0] text-slate-700 font-bold text-[10.5px] leading-none flex items-center justify-center overflow-hidden shrink-0 ${
                                                                            iden.isLocked || _sh?.isLocked ? 'opacity-60 ring-1 ring-slate-300' : ''
                                                                        }`}
                                                                    >
                                                                        {iden.photoURL ? (
                                                                            <img src={iden.photoURL} alt={iden.name} className="w-full h-full object-cover" />
                                                                        ) : (
                                                                            <span className="tracking-tight">{iden.initials}</span>
                                                                        )}
                                                                    </div>
                                                                );
                                                            })}
                                                            {claimants.length > 3 && (
                                                                <div className="w-[26px] h-[26px] rounded-full bg-slate-100 text-slate-600 font-bold text-[10px] leading-none flex items-center justify-center shrink-0">
                                                                    +{claimants.length - 3}
                                                                </div>
                                                            )}
                                                        </div>
                                                        <div className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-900 text-xs font-bold transition-colors shrink-0">
                                                            <Plus className="w-3 h-3 stroke-[2.5]" />
                                                            <span>I had this</span>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Section 4: Claimed by others */}
                    {claimedByOthers.length > 0 && (
                        <div className="space-y-2.5">
                            <div className="flex items-center gap-2 px-1">
                                <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">
                                    CLAIMED BY OTHERS
                                </span>
                                <span className="w-5 h-5 rounded-full bg-[#E2E8F0] text-[#334155] font-bold text-[11px] flex items-center justify-center">
                                    {claimedByOthers.length}
                                </span>
                            </div>

                            <div className="bg-white rounded-3xl overflow-hidden divide-y divide-slate-100/70">
                                {claimedByOthers.map((item) => {
                                    const shares = item.shares || {};
                                    const rawShares = Object.entries(shares).filter(([_, v]: any) => getShareFraction(v) > 0.0001);
                                    const shareKeys = sortShareEntries(rawShares, participantOrderMap);
                                    const visibleShareKeys = shareKeys.slice(0, 3);
                                    const overflowCount = Math.max(0, shareKeys.length - 3);
                                    const fullyLocked = isItemFullyLocked(item);

                                    return (
                                        <div key={item.id} className="p-4 flex items-center gap-3.5 hover:bg-slate-50/50 transition-colors">
                                            <WebItemIconAvatar name={item.name} iconKey={item.iconKey} size={40} />
                                            <div className="min-w-0 flex-1 space-y-1.5">
                                                {/* Line 1: Title and Total Amount */}
                                                <div className="flex items-center justify-between gap-2">
                                                    <p className="font-extrabold text-sm text-slate-900 truncate">{item.name}</p>
                                                    <p className="font-black text-sm text-slate-500 shrink-0">${Number(item.amount || 0).toFixed(2)}</p>
                                                </div>

                                                {/* Line 2: Claimant Avatars & Label on left, Join split / Paid badge on right */}
                                                <div className="flex items-center justify-between gap-2">
                                                    <div className="flex items-center gap-2 min-w-0">
                                                        {shareKeys.length > 0 && (
                                                            <div className="flex items-center gap-1.5 overflow-visible shrink-0">
                                                                {visibleShareKeys.map(([ph, _sh]: any) => {
                                                                    const iden = getParticipantDisplay(ph, _sh?.name);
                                                                    return (
                                                                        <div
                                                                            key={ph}
                                                                            className={`w-[26px] h-[26px] rounded-full bg-[#E2E8F0] text-slate-700 font-bold text-[10.5px] leading-none flex items-center justify-center overflow-hidden shrink-0 ${
                                                                                iden.isLocked || _sh?.isLocked ? 'opacity-60 ring-1 ring-slate-300' : ''
                                                                            }`}
                                                                        >
                                                                            {iden.photoURL ? (
                                                                                <img src={iden.photoURL} alt={iden.name} className="w-full h-full object-cover" />
                                                                            ) : (
                                                                                <span className="tracking-tight">{iden.initials}</span>
                                                                            )}
                                                                        </div>
                                                                    );
                                                                })}
                                                                {overflowCount > 0 && (
                                                                    <div className="w-[26px] h-[26px] rounded-full bg-slate-100 text-slate-600 font-bold text-[10px] leading-none flex items-center justify-center shrink-0">
                                                                        +{overflowCount}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}
                                                        <p className="text-xs font-semibold text-slate-500 truncate">
                                                            {shareKeys.length === 1
                                                                ? (getParticipantDisplay(shareKeys[0][0], shareKeys[0][1]?.name).name || '1 person')
                                                                : `${shareKeys.length} people`}
                                                        </p>
                                                    </div>

                                                    {fullyLocked ? (
                                                        <div className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-slate-100 text-slate-400 text-xs font-semibold shrink-0 select-none cursor-default">
                                                            <Lock className="w-3 h-3 text-slate-400 stroke-[2.2]" />
                                                            <span>Paid</span>
                                                        </div>
                                                    ) : (
                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                triggerUiHaptic('selection');
                                                                setHowSharedItem(item);
                                                            }}
                                                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-semibold transition-colors shrink-0 cursor-pointer"
                                                        >
                                                            <Users className="w-3 h-3" />
                                                            <span>Join split</span>
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Sticky Financial Footer */}
            <div
                ref={footerRef}
                className="fixed bottom-0 inset-x-0 bg-white/95 backdrop-blur-xl border-t border-slate-200/80 px-4 pt-3 pb-6 z-30 shadow-[0_-8px_24px_rgba(15,23,42,0.06)]"
            >
                <div className="max-w-md mx-auto space-y-3">
                    {/* Financial Summary Box */}
                    <div className="bg-[#F8FAFC] rounded-2xl p-4 space-y-2 text-xs">
                        <div className="flex justify-between font-semibold text-slate-600">
                            <span>Your items</span>
                            <span className="font-bold text-slate-900">${viewerOwnTotal.toFixed(2)}</span>
                        </div>
                        {coveredSectionsByPerson.length > 3 ? (
                            <div className="max-h-28 overflow-y-auto overscroll-contain space-y-2 pr-0.5">
                                {coveredSectionsByPerson.map((section) => (
                                    <div key={`cov_summary_${section.person.phone || section.person.id}`} className="flex justify-between font-semibold text-slate-600">
                                        <span>Covering for {section.person.name}</span>
                                        <span className="font-bold text-slate-900">${section.subtotal.toFixed(2)}</span>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            coveredSectionsByPerson.map((section) => (
                                <div key={`cov_summary_${section.person.phone || section.person.id}`} className="flex justify-between font-semibold text-slate-600">
                                    <span>Covering for {section.person.name}</span>
                                    <span className="font-bold text-slate-900">${section.subtotal.toFixed(2)}</span>
                                </div>
                            ))
                        )}
                        {viewerExtrasTotal !== 0 && (
                            <div className="flex justify-between font-semibold text-slate-600">
                                <span>Tax & extras</span>
                                <span className="font-bold text-slate-900">${viewerExtrasTotal.toFixed(2)}</span>
                            </div>
                        )}
                        <div className="h-px bg-slate-200/70 my-1" />
                        <div className="flex justify-between items-center">
                            <span className="font-extrabold text-sm text-slate-900">Total (You owe)</span>
                            <span className="font-black text-base text-[#0D9488]">${viewerNetTotal.toFixed(2)}</span>
                        </div>
                        {(() => {
                            const ai = session?.aiData;
                            const topCurrency = session?.originalCurrency;
                            const topAmount = session?.originalAmount;
                            const origCurrency = String(topCurrency !== undefined && topCurrency !== null ? topCurrency : (ai?.original_currency || ai?.originalCurrency || '')).trim().toUpperCase();
                            const origAmount = Number(topAmount !== undefined && topAmount !== null ? topAmount : (ai?.original_amount ?? ai?.originalAmount ?? 0));
                            if (origCurrency && origCurrency !== 'USD' && origAmount > 0) {
                                return (
                                    <div className="flex justify-between items-center text-xs font-semibold text-slate-400 mt-1">
                                        <span>Original Receipt</span>
                                        <span>{origCurrency} {origAmount.toFixed(2)}</span>
                                    </div>
                                );
                            }
                            return null;
                        })()}
                    </div>

                    {/* Save Error Alert */}
                    {saveError && (
                        <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-2xl flex items-center justify-between text-xs font-bold text-red-700">
                            <span>{saveError}</span>
                            <button type="button" onClick={() => setSaveError(null)} className="text-red-500 hover:text-red-800 p-1">
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                    )}

                    {/* Bottom CTA Row */}
                    <div className="flex items-center gap-3">
                        {receiptUrl && (
                            <button
                                type="button"
                                onClick={() => {
                                    triggerUiHaptic('selection');
                                    onViewReceipt?.();
                                }}
                                className="h-12 min-h-[48px] w-12 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center text-slate-700 shrink-0 transition-all active:scale-[0.98] overflow-hidden"
                                aria-label="View receipt"
                            >
                                {receiptUrl && !receiptUrl.startsWith('file://') ? (
                                    <img src={receiptUrl} alt="Receipt" className="w-full h-full object-cover" />
                                ) : (
                                    <Receipt className="w-5 h-5 text-slate-600" />
                                )}
                            </button>
                        )}
                        <div className="flex-1">
                            <WebCta
                                title={hasUnsavedChanges ? 'Save changes' : 'Done'}
                                variant="contrast"
                                loading={isSaving}
                                onClick={handleSaveAndCommit}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Child Sheets */}
            <WebWhoHadThisSheet
                visible={Boolean(whoHadThisItem)}
                onClose={() => setWhoHadThisItem(null)}
                item={whoHadThisItem}
                participants={allSessionParticipants}
                viewerPhone={userPhone}
                viewerDisplayName={invite?.name || 'You'}
                viewerPhotoURL={invite?.photoURL || invite?.photoUrl}
                organizerPhone={session?.organizerPhone}
                organizerName={organizerDisplayName}
                organizerPhotoURL={session?.organizerPhotoURL || session?.organizerPhotoUrl}
                session={session}
                onSaveAssignment={handleSaveAssignment}
                getItemIconEmoji={getItemIconEmoji}
            />

            <WebHowWasThisSharedSheet
                visible={Boolean(howSharedItem)}
                onClose={() => setHowSharedItem(null)}
                item={howSharedItem}
                userPhone={userPhone}
                organizerPhone={session?.organizerPhone}
                participants={allSessionParticipants}
                onJoinSplit={handleJoinSplit}
                onClaimFull={handleClaimFull}
                onShareWithSomeone={(id) => {
                    const it = items.find((x) => String(x.id) === String(id));
                    setHowSharedItem(null);
                    setWhoHadThisItem(it || null);
                }}
                getItemIconEmoji={getItemIconEmoji}
            />

            <WebCoverSomeoneSheet
                visible={showCoverSheet}
                onClose={() => setShowCoverSheet(false)}
                userPhone={userPhone}
                organizerPhone={session?.organizerPhone}
                participants={allSessionParticipants}
                currentCoveredPhones={coveredPersons.map((p) => p.phone)}
                onSelectCoveredPersons={(selected) => setCoveredPersons(selected)}
            />

            {/* Unsaved Changes Discard Modal */}
            {showUnsavedDiscardModal && (
                <div className="fixed inset-0 z-[100] bg-slate-950/60 backdrop-blur-sm flex items-center justify-center p-4">
                    <div className="w-full max-w-xs bg-white rounded-3xl p-6 text-center space-y-4 shadow-2xl animate-in zoom-in-95 duration-200">
                        <h3 className="font-extrabold text-lg text-slate-900">Unsaved Changes</h3>
                        <p className="text-xs text-slate-500 font-medium leading-relaxed">
                            You have unsaved item claims. Do you want to save them before leaving?
                        </p>
                        <div className="space-y-2 pt-2">
                            <WebCta
                                title="Save & Exit"
                                variant="contrast"
                                onClick={() => {
                                    setShowUnsavedDiscardModal(false);
                                    void handleSaveAndCommit();
                                }}
                            />
                            <WebCta
                                title="Discard"
                                variant="neutral"
                                onClick={() => {
                                    setShowUnsavedDiscardModal(false);
                                    handleSafeClose();
                                }}
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default WebReviewItemsView;
